import React, { useState, useEffect } from 'react';
import { Card, Alert, Spin, Typography, Space, Empty, List } from 'antd';
import { EyeOutlined, ClockCircleOutlined, BookOutlined, FileTextOutlined } from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const { Title, Text, Paragraph } = Typography;

const StaffPreviewEducation = ({ patientId }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [educationData, setEducationData] = useState(null);
  const [staffPreviewInfo, setStaffPreviewInfo] = useState(null);

  useEffect(() => {
    const fetchPreviewData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch staff preview data using clinical authentication
        const response = await axios.get(`/portal/admin/preview/${patientId}/education`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        const data = response.data;
        
        // Extract staff preview metadata
        if (data._staffPreview) {
          setStaffPreviewInfo(data._staffPreview);
          // Remove metadata from the data
          const { _staffPreview, ...portalData } = data;
          setEducationData(portalData);
        } else {
          setEducationData(data);
        }

      } catch (err) {
        console.error('Error fetching staff preview education:', err);
        setError(err.response?.data?.message || 'Failed to load education preview');
      } finally {
        setLoading(false);
      }
    };

    if (patientId && token) {
      fetchPreviewData();
    }
  }, [patientId, token]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Preview Error"
        description={error}
        type="error"
        showIcon
        style={{ margin: '20px' }}
      />
    );
  }

  return (
    <div>
      {/* Staff Preview Info Card */}
      {staffPreviewInfo && (
        <Card 
          size="small" 
          style={{ 
            marginBottom: '20px',
            background: 'linear-gradient(135deg, #e6f7ff 0%, #bae7ff 100%)',
            border: '1px solid #91d5ff'
          }}
        >
          <Space>
            <EyeOutlined style={{ color: '#1890ff' }} />
            <Text strong style={{ color: '#1890ff' }}>Staff Preview Mode - Education</Text>
            <Text type="secondary">|</Text>
            <Text type="secondary">
              <ClockCircleOutlined style={{ marginRight: '4px' }} />
              {new Date(staffPreviewInfo.previewedAt).toLocaleString()}
            </Text>
            <Text type="secondary">|</Text>
            <Text type="secondary">Previewed by: {staffPreviewInfo.previewedBy}</Text>
          </Space>
        </Card>
      )}

      {/* Education Content */}
      <div style={{ 
        border: '2px dashed #d9d9d9',
        borderRadius: '8px',
        padding: '20px',
        background: '#fafafa',
        minHeight: '400px'
      }}>
        <div style={{ 
          background: '#fff',
          borderRadius: '6px',
          padding: '20px'
        }}>
          <Title level={3} style={{ marginBottom: '20px' }}>
            <BookOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
            Patient Education Materials
          </Title>
          
          {educationData && educationData.data && educationData.data.length > 0 ? (
            <List
              itemLayout="vertical"
              dataSource={educationData.data}
              renderItem={(item, index) => (
                <List.Item key={index}>
                  <Card 
                    size="small"
                    title={
                      <Space>
                        <FileTextOutlined style={{ color: '#52c41a' }} />
                        <Text strong>{item.title || `Education Material ${index + 1}`}</Text>
                      </Space>
                    }
                    style={{ marginBottom: '12px' }}
                  >
                    {item.description && (
                      <Paragraph style={{ marginBottom: '12px' }}>
                        {item.description}
                      </Paragraph>
                    )}
                    
                    {item.content && (
                      <div style={{ 
                        background: '#f9f9f9',
                        padding: '12px',
                        borderRadius: '4px',
                        border: '1px solid #e8e8e8'
                      }}>
                        <Text>{item.content}</Text>
                      </div>
                    )}
                    
                    {item.category && (
                      <div style={{ marginTop: '8px' }}>
                        <Text type="secondary">Category: {item.category}</Text>
                      </div>
                    )}
                    
                    {item.lastUpdated && (
                      <div style={{ marginTop: '4px' }}>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          Last updated: {new Date(item.lastUpdated).toLocaleDateString()}
                        </Text>
                      </div>
                    )}
                  </Card>
                </List.Item>
              )}
            />
          ) : (
            <Empty 
              description={
                <div>
                  <Text>No education materials available for this patient</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Education materials would appear here when available
                  </Text>
                </div>
              }
              style={{ padding: '40px' }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default StaffPreviewEducation;
